/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** DID定義 */
const DID_HORIZONTAL = "10A4";
const DID_VERTICAL = "10A5";

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: 'standby', // 待機
  EXECUTE: "execute", // 実行
  EXECUTE_CHECK: "executeCheck", // 実行監視
  STOP: "stop", // 停止
  ADJUSTMENT_FAILED: "adjustmentFailed", // 調整失敗の表示
  INCOMPLETE_ENVIRONMENT: "incompleteEnvironment", // 調整環境不備の表示
  ERROR: "error",//共通エラー表示
};

export default {
  name: "requirement168",
  data() {
    return {
      phase: PHASE.STANDBY,
      isDispose: true,  // NonDefaultSession遷移成否
      isLoading: false,
      exitProcessPromise: null,
      systemId: "",
      horizontalDeg: null,
      verticalDeg: null,
      modal_requirement_168: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    headerColor() {
      switch (this.phase) {
        case PHASE.ADJUSTMENT_FAILED:
        case PHASE.INCOMPLETE_ENVIRONMENT:
        case PHASE.ERROR:
          return "danger";
        case PHASE.STOP:
          return "success";
        case PHASE.STANDBY:
        case PHASE.EXECUTE:
        case PHASE.EXECUTE_CHECK:
        default:
          return "secondary";
      }
    },

    headerTitle() {
      // 実行中
      const executionTitle = 'individual.common_title_execution';
      // 確認
      const confirmationTitle = 'message.common_title_confirmation';
      // 成功
      const successTitle = 'message.common_title_success';
      // エラー
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.STANDBY]: confirmationTitle,
        [PHASE.EXECUTE]: confirmationTitle,
        [PHASE.EXECUTE_CHECK]: executionTitle,
        [PHASE.STOP]: successTitle,
        [PHASE.ADJUSTMENT_FAILED]: errorTitle,
        [PHASE.INCOMPLETE_ENVIRONMENT]: errorTitle,
        [PHASE.ERROR]: errorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        [PHASE.STANDBY]: `${this.i18nWsKey}.REQ_WS_168_TXT_1`,
        [PHASE.EXECUTE]: `${this.i18nWsKey}.REQ_WS_168_TXT_2`,
        [PHASE.EXECUTE_CHECK]: `${this.i18nWsKey}.REQ_WS_168_TXT_3_0001`,
        [PHASE.STOP]: `${this.i18nWsKey}.REQ_WS_168_TXT_4_1100`,
        [PHASE.ADJUSTMENT_FAILED]: `${this.i18nWsKey}.REQ_WS_168_TXT_5_1101`,
        [PHASE.INCOMPLETE_ENVIRONMENT]: `${this.i18nWsKey}.REQ_WS_168_TXT_6_1102`,
        [PHASE.ERROR]: 'individual.error_communication_error',
      };
      switch (this.phase) {
        case PHASE.ADJUSTMENT_FAILED: {
          return this.$t(messageMap[this.phase]).replace('$DID$', this.horizontalDeg).replace('$DID$', this.verticalDeg);
        }
        default:
          break;
      }
      return this.$t(messageMap[this.phase]);
    },

    showButtonYesNo() {
      const showYesNoButtonMap = {
        [PHASE.STANDBY]: true,
        [PHASE.EXECUTE]: false,
        [PHASE.EXECUTE_CHECK]: false,
        [PHASE.STOP]: false,
        [PHASE.ADJUSTMENT_FAILED]: false,
        [PHASE.INCOMPLETE_ENVIRONMENT]: false,
        [PHASE.ERROR]: false,
      };
      return showYesNoButtonMap[this.phase] ?? false;
    },

    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.STANDBY]: false,
        [PHASE.EXECUTE]: true,
        [PHASE.EXECUTE_CHECK]: false,
        [PHASE.STOP]: true,
        [PHASE.ADJUSTMENT_FAILED]: true,
        [PHASE.INCOMPLETE_ENVIRONMENT]: true,
        [PHASE.ERROR]: true,
      };
      return showOkButtonMap[this.phase] ?? false;
    },

    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.STANDBY]: false,
        [PHASE.EXECUTE]: true,
        [PHASE.EXECUTE_CHECK]: false,
        [PHASE.STOP]: false,
        [PHASE.ADJUSTMENT_FAILED]: false,
        [PHASE.INCOMPLETE_ENVIRONMENT]: false,
        [PHASE.ERROR]: false,
      };
      return showCancelButtonMap[this.phase] ?? false;
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_168 = true;
    },

    /**
     * Wizard を閉じる。close-wizard イベントを emit しモーダルを非表示にする。
     */
    closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_168 = false;
    },

    /**
     * 予期しないエラー発生時の表示処理。
     * exitProcess を呼び出してルーチンを終了させた後、phase を ERROR に遷移させる。
     */
    async showUnexpectedError() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitProcessPromise = this.exitProcess();
      await this.exitProcessPromise;
      // 共通エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {object} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /**
     * ルーチン終了処理。isDispose フラグに応じて dispose または terminate API を呼び出す。
     */
    async exitProcess() {
      if (this.isDispose) {
        await this.dispose();
      } else {
        await this.terminate();
      }
    },

    /**
     * キャンセルボタン押下時の処理。Wizard を閉じる。
     */
    onCancelClicked() {
      this.closeWizard();
    },

    /**
     * いいえボタン押下時の処理。Wizard を閉じる。
     */
    onNoClicked() {
      this.closeWizard();
    },

    /**
     * はいボタン押下時の処理。phase を EXECUTE に遷移させる。
     */
    onYesClicked() {
      this.phase = PHASE.EXECUTE;
    },

    /**
     * OKボタン押下時の処理。現在の phase に応じて Wizard を閉じるか execute を実行する。
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.STOP:
        case PHASE.ADJUSTMENT_FAILED:
        case PHASE.INCOMPLETE_ENVIRONMENT:
        case PHASE.ERROR:
          this.closeWizard();
          break;
        case PHASE.EXECUTE:
          await this.execute();
          break;
        default:
          break;
      }
    },

    /** ルーチン実行 */
    async execute() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitProcessPromise) await this.exitProcessPromise;
          return;
        }
        this.isDispose = response.data.user_data.is_dispose;
        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          await this.executeCheck(); // 正常終了：実行監視画面に遷移
        } else {
          await this.showUnexpectedError(); // 異常終了：共通エラーに遷移
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** ルーチン結果確認 */
    async executeCheck() {
      try {
        this.isLoading = true;
        this.phase = PHASE.EXECUTE_CHECK;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'monitor'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitProcessPromise) await this.exitProcessPromise;
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED: // 正常終了
            await this.terminate();
            this.phase = PHASE.STOP;
            break;
          case REQUEST_STATUS_FAILED: // 異常終了
            switch (response.data.async_code) {
              case error.WSP_168_ADJUSTMENT_FAILED:
                await this.readWithSession(); // 調整失敗表示へ遷移
                break;
              case error.WSP_168_ADJUSTMENT_ENVIRONMENT_FAILED:
                await this.terminate();
                this.phase = PHASE.INCOMPLETE_ENVIRONMENT; // 調整環境不備の表示へ遷移
                break;
              default:
                await this.showUnexpectedError(); // 共通エラーへ遷移
            }
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 調整失敗の表示処理。
     * read_with_session API を呼び出し、取得した DID データを基に水平・垂直の角度値を算出して
     * horizontalDeg / verticalDeg に設定する。exitProcessPromise は呼ばず、phase を ADJUSTMENT_FAILED に遷移させる。
     */
    async readWithSession() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read_with_session'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitProcessPromise) await this.exitProcessPromise;
          return;
        }

        if (response.data.status !== REQUEST_STATUS_COMPLETED) {
          await this.showUnexpectedError(); // 異常終了：共通エラーに遷移
          return;
        }
        const userData = response.data.user_data;

        // DIDのByte1要素を取得
        const horizontalUserData = { readout_data: (userData?.readout_data ?? []).filter(item => item.did?.startsWith(DID_HORIZONTAL)) };
        const verticalUserData = { readout_data: (userData?.readout_data ?? []).filter(item => item.did?.startsWith(DID_VERTICAL)) };

        // 表示用DID編集処理
        const horizontalReadOutData = await individualWorkSupportUtil.getReadOutData(this, this.systemId, horizontalUserData);
        const verticalReadOutData = await individualWorkSupportUtil.getReadOutData(this, this.systemId, verticalUserData);

        // DID編集後のデータから値取得（正規表現で先頭の数値部分を抽出）
        const horizontalParts = horizontalReadOutData ? horizontalReadOutData.split(':') : [];
        this.horizontalDeg = (horizontalParts.length > 1 ? horizontalParts[1] : '0').match(/^-?[\d.]+/)?.[0] ?? '0';
        const verticalParts = verticalReadOutData ? verticalReadOutData.split(':') : [];
        this.verticalDeg = (verticalParts.length > 1 ? verticalParts[1] : '0').match(/^-?[\d.]+/)?.[0] ?? '0';

        // 調整失敗の表示へ遷移
        this.phase = PHASE.ADJUSTMENT_FAILED;

      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ルーチン終了（terminate）API を呼び出す。
     * is_dispose が false の場合に exitProcess から呼ばれる。レスポンス内容は参照しない。
     */
    async terminate() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'terminate'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ルーチン破棄（dispose）API を呼び出す。
     * is_dispose が true の場合に exitProcess から呼ばれる。レスポンス内容は参照しない。
     */
    async dispose() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'dispose'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result, 'dispose'); },
        );
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.init();
  }
};
